import React, { Fragment, FunctionComponent, useEffect, useState } from 'react';
import styled from 'styled-components';
import { SpaceProps } from 'styled-system';
import { Field } from 'react-final-form';
import { InputField, Text } from 'mdlkit';
import { useIntl } from 'react-intl';
import { useTheme } from '../../../core/hooks';
import { required } from '../../../utils/shared/validations';
import MemberIdTooltip from './MemberIdTooltip';
import getAffiliationEligibilityDetailsAsync from '../../../api/affiliations/getAffiliationEligibilityDetailsAsync';

type MemberIdProps = {
  affiliationId?: number;
} & SpaceProps;

const StyledText = styled(Text)`
  align-self: start;
`;

const FieldContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-top: ${({ theme }) => theme.space.sm}px;
  margin-bottom: ${({ theme }) => theme.space.sm}px;
`;

const FieldLabel = styled.div`
  margin-bottom: ${({ theme }) => theme.space.xs}px;
`;

const IconContainer = styled.div`
  position: absolute;
  right: 0;
  top: 0;
`;

const IconText = styled(Text)`
  margin-left: 25px;
  position: relative;
  bottom: 22px;
`;

const MemberId: FunctionComponent<MemberIdProps> = ({
  affiliationId,
  ...rest
}: MemberIdProps) => {
  const [insuranceLabel, setInsuranceLabel] = useState<null | string>(null);
  const [tooltipImage, setTooltipImage] = useState<null | string>(null);

  const { formatMessage: f } = useIntl();
  const theme = useTheme();

  useEffect(() => {
    const timer = setTimeout(() => {
      const el = document.getElementById('memberId');
      if (el) el.focus();
    });

    if (affiliationId) {
      getAffiliationEligibilityDetailsAsync(affiliationId).then(
        ({ affiliationEligibilityDetail }) => {
          if (typeof affiliationEligibilityDetail !== 'undefined') {
            setInsuranceLabel(affiliationEligibilityDetail.insuranceLabel);
            setTooltipImage(affiliationEligibilityDetail.tooltipImage);
          } else {
            setInsuranceLabel(null);
            setTooltipImage(null);
          }
        }
      );
    }

    return () => clearTimeout(timer);
  }, [affiliationId]);

  return (
    <Fragment>
      <FieldContainer>
        <FieldLabel>
          <Text bold>{f({ id: 'register.memberId' })}</Text>
          {tooltipImage && (
            <IconContainer>
              <MemberIdTooltip tooltipImage={tooltipImage} />
              <IconText small>See An Example</IconText>
            </IconContainer>
          )}
        </FieldLabel>
        <Field
          pt={theme.space.sm + theme.space.xs}
          id="memberId"
          name="memberId"
          component={InputField}
          validate={required}
          autoCorrect="off"
          spellCheck={false}
          {...rest}
        />
      </FieldContainer>
      {insuranceLabel ? (
        <StyledText small pt="xxs" data-testid="memberIdHelp">
          Insurance Member ID should&nbsp;
          {insuranceLabel}
        </StyledText>
      ) : null}
    </Fragment>
  );
};

export default MemberId;
